import {Color} from '@benchling/nucleus-ui';
import {Badge as MantineBadge, MantineColor, createStyles} from '@mantine/core';
import React, {ReactNode} from 'react';

import {LEGACY_FONT_PROXIMA_12} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {
  BLUE,
  BLUE_LIGHT,
  BLUE_POWDER,
  GRAY_DARKER,
  GRAY_LIGHT,
  GREEN,
  GREEN_LIGHTER,
  GREEN_DARKER,
  PURPLE_DARK,
  PURPLE_LIGHTER,
  RED,
  RED_POWDER,
  YELLOW_DARKER,
  YELLOW_LIGHTER,
} from 'ui-library/Colors';

import BenchlingIcon, {BenchlingIconName} from './icons/BenchlingIcon';

export enum BadgeColor {
  BLUE = 'blue',
  COLLECTIBLE_BLUE = 'collectible-blue',
  GRAY = 'gray',
  GREEN = 'green',
  GREEN_FILLED = 'green-filled',
  RED = 'red',
  PURPLE = 'purple',
  YELLOW = 'yellow',
}

const BADGE_COLOR_TO_MANTINE_COLOR: Record<BadgeColor, MantineColor> = {
  [BadgeColor.BLUE]: 'blue',
  [BadgeColor.COLLECTIBLE_BLUE]: 'blue',
  [BadgeColor.GRAY]: 'gray',
  [BadgeColor.GREEN]: 'green',
  [BadgeColor.GREEN_FILLED]: 'green',
  [BadgeColor.RED]: 'red',
  [BadgeColor.PURPLE]: 'purple',
  [BadgeColor.YELLOW]: 'yellow',
};

const useStyles = createStyles(() => ({
  blue: {
    backgroundColor: BLUE_POWDER,
    color: BLUE_LIGHT,
    '&:hover, &:active': {
      backgroundColor: BLUE_POWDER,
      color: BLUE_LIGHT,
    },
  },
  collectibleBlue: {
    backgroundColor: Color.BLUE_150,
    color: BLUE,
    '&:hover, &:active': {
      backgroundColor: Color.BLUE_150,
      color: BLUE,
    },
  },
  red: {
    backgroundColor: RED_POWDER,
    color: RED,
    '&:hover, &:active': {backgroundColor: RED_POWDER, color: RED},
  },
  gray: {
    backgroundColor: GRAY_LIGHT,
    color: GRAY_DARKER,
    '&:hover, &:active': {backgroundColor: GRAY_LIGHT, color: GRAY_DARKER},
  },
  green: {
    backgroundColor: GRAY_LIGHT,
    color: GREEN,
    '&:hover, &:active': {backgroundColor: GRAY_LIGHT, color: GREEN},
  },
  greenFilled: {
    backgroundColor: GREEN_LIGHTER,
    color: GREEN_DARKER,
    '&:hover, &:active': {backgroundColor: GREEN_LIGHTER, color: GREEN_DARKER},
  },
  purple: {
    backgroundColor: PURPLE_LIGHTER,
    color: PURPLE_DARK,
    '&:hover, &:active': {backgroundColor: PURPLE_LIGHTER, color: PURPLE_DARK},
  },
  yellow: {
    backgroundColor: YELLOW_LIGHTER,
    color: YELLOW_DARKER,
    '&:hover, &:active': {backgroundColor: YELLOW_LIGHTER, color: YELLOW_DARKER},
  },
  badge: {
    borderRadius: 4,
    padding: 6,
    textTransform: 'uppercase',
  },
  font: {
    ...LEGACY_FONT_PROXIMA_12,
  },
  bold: {
    fontWeight: 'bold',
  },
}));

interface Props {
  className?: string;
  children: ReactNode;
  color: BadgeColor;
  bold?: boolean;
  startingIcon?: BenchlingIconName;
  dataTestAttrs?: DataTestAttrs;
}

/** @deprecated Use Mantine Badge directly instead */
export default function Badge(props: Props): JSX.Element {
  const {classes, cx} = useStyles();

  const colorStyleMap: Partial<Record<BadgeColor, string>> = {
    [BadgeColor.BLUE]: classes.blue,
    [BadgeColor.COLLECTIBLE_BLUE]: classes.collectibleBlue,
    [BadgeColor.GRAY]: classes.gray,
    [BadgeColor.GREEN]: classes.green,
    [BadgeColor.GREEN_FILLED]: classes.greenFilled,
    [BadgeColor.RED]: classes.red,
    [BadgeColor.PURPLE]: classes.purple,
    [BadgeColor.YELLOW]: classes.yellow,
  };

  const icon = props.startingIcon ? <BenchlingIcon icon={props.startingIcon} /> : undefined;
  return (
    <MantineBadge
      classNames={{
        root: cx(props.className, classes.badge, colorStyleMap[props.color], props.bold && classes.bold),
        inner: classes.font,
      }}
      color={BADGE_COLOR_TO_MANTINE_COLOR[props.color]}
      leftSection={icon}
      size="xl"
      styles={{leftSection: {marginRight: '4px'}}}
      variant="light"
      {...getTestProps(props.dataTestAttrs)}
    >
      {props.children}
    </MantineBadge>
  );
}
